<template>
  <draggable
    v-bind="$attrs"
    tag="ul"
    v-model="computedItems"
    :group="`${groupPrefix}_${level}`"
    @add="onAdd"
    @update="onUpdate"
    :index="0"
    :move="onMove"
    :scrollSensitivity="200"
    :forceFallback="true"
    handle=".handleDrag"
  >
    <li
      v-for="(el, idx) in computedItems"
      :key="el.id"
      :id="el.id || idx"
      :class="[...classes, el.required ? 'required' : '', level === 1 && !el.isHidden ? 'pb-2' : '']"
    >
      <slot
        name="listElement"
        v-bind="{
          ...el,
          level,
          dotsItems,
          listeners: $listeners,
          order: index || idx,
          parentLabel: parentLabel,
          index: idx,
        }"
      />
      <draggable-lists-nested
        v-bind="$attrs"
        :default_value_policy="el.default_value_policy"
        :parent-label="el.label"
        :is-layers="isLayers"
        v-if="el[childPropName] && !el.isHidden"
        :items="el[childPropName]"
        :level="level + 1"
        :dotsItems="layersDotsItems"
        v-on="$listeners"
        :index="idx"
        :multi-drag="!disableMultiDrag"
        ghost-class="draggable-selected"
        selected-class="draggable-selected"
        class="pl-6"
      >
        <template v-for="(value, key) in $scopedSlots" #[key]="scope">
          <slot :name="key" v-bind="{ ...scope, idx }" />
        </template>
      </draggable-lists-nested>
    </li>
  </draggable>
</template>

<script>
import draggable from 'vuedraggable-multi';

export default {
  name: 'DraggableListsNested',
  components: {
    draggable,
  },
  props: {
    default_value_policy: {
      type: Object,
      default: () => ({}),
    },
    parentLabel: {
      type: String,
      default: '',
    },
    isLayers: {
      type: Boolean,
      default: true,
    },
    childPropName: {
      type: String,
      default: 'layers',
    },
    index: {
      type: Number,
      default: 0,
    },
    items: {
      required: true,
      type: Array,
    },
    level: {
      type: Number,
      required: true,
    },
    dotsItems: {
      type: Array,
      default: () => [{ name: 'changeGroupName' }, { name: 'delete' }],
    },
    layersDotsItems: {
      type: Array,
      default: () => [
        {
          name: 'manageLayer',
        },
        {
          name: 'manageCard',
        },
        {
          name: 'changeSymbolization',
        },
        {
          name: 'copyLayer',
        },
        {
          name: 'deleteFromSystem',
        },
      ],
    },
    disableMultiDrag: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    groupPrefix() {
      return this.isLayers ? 'layers' : 'attributes';
    },
    classes() {
      return this.level === 1 ? ['li--group', 'pr-6'] : ['border'];
    },
    computedItems: {
      get() {
        return this.items;
      },
      set(nV) {
        this.setValue(nV);
      },
    },
  },
  methods: {
    setValue(value) {
      if (!this.isLayers) {
        if (this.level === 1) {
          this.$store.set('layers/SET_CURRENT_LAYER_SCHEMA!', value);
        } else {
          this.$store.set('layers/EDIT_CURRENT_LAYER_SCHEMA_GROUP!', {
            index: this.index,
            value: {
              elements: value,
            },
          });
        }
      }
    },
    onAdd(e) {
      this.$emit('elementAdded', {
        order_in_group: e.newIndex,
        toGroup: e.to.parentNode.id,
        elementId:
          e.items.length > 0
            ? [
                ...e.items.map(x => {
                  return { layer_id: x.id };
                }),
              ]
            : [{ layer_id: e.item.id }],
      });
    },
    onMove(e) {
      if (this.isLayers) {
        return;
      }
      if (e.to.id === 'list--right' && [...[e.draggedContext].flat()].some(x => x.element.required)) {
        return false;
      }
    },
    onUpdate(e) {
      const isGroup = this.level === 1;
      this.$emit('elementAdded', {
        isGroup,
        order_in_group: e.newIndex,
        toGroup: e.to.parentNode.id,
        elementId: isGroup
          ? [e.item.id]
          : e.items.length > 0
          ? [
              ...e.items.map(x => {
                return {
                  layer_id: x.id,
                  cache: this.items.find(item => item.id === +x.id)?.cache || false,
                };
              }),
            ]
          : [
              {
                layer_id: e.item.id,
                cache: this.items.find(item => item.id === +e.item.id)?.cache || false,
              },
            ],
      });
    },
  },
};
</script>

<style scoped lang="scss">
ul {
  list-style: none;
  padding-left: 0;
}
.li--group {
  @include border(top);
  @include border(right);
  @include border(left);
  &:last-child {
    @include border(bottom);
  }
}
::v-deep .draggable-selected .element {
  @include draggableSelectedElement();
}
</style>
